import { faArrowRight, faChevronDown, faChevronUp, faPlay, faVolumeUp, faVolumeMute } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import { SuccessStories as placementSuccessStories} from '../utilities/constants'
import { motion, AnimatePresence, useInView } from 'motion/react'
import { Dialog } from '@headlessui/react'
import ReactPlayer from 'react-player'

const SuccessStory = () => {
    
    const [showWatchText, setShowWatchText] = useState(false)
    const [modalEnabled , setModalEnabled] = useState({is_show : false , achievement:{ name: '', description: '', from: '', to: '', thumbNail: '', videoUrl: '', thumbNail2: ''}})
    const [isScrolledDown, setIsScrolledDown] = useState(false)
    const [isVideoPlaying, setIsVideoPlaying] = useState(false) // Track video playing state
    const [isMuted, setIsMuted] = useState(false) // Add this state for mute control
    const [isModalVideoPlaying, setIsModalVideoPlaying] = useState(true) // Add this for modal video state
    
    // Create refs for the first slide and video to detect when they come into view
    const firstSlideRef = useRef(null)
    const videoRef = useRef(null) // Add ref for video element
    const swiperRef = useRef(null)
    const modalContainerRef = useRef(null)
    const playTimeoutRef = useRef(null) // For delayed play
    
    const isInView = useInView(firstSlideRef, { 
        once: true, 
        margin: "-100px 0px -100px 0px" 
    })
    
    // Add useInView for video specifically
    const isVideoInView = useInView(videoRef, { 
        once: false, // Allow multiple triggers
        margin: "-50px 0px -50px 0px" // Smaller margin for video
    })

    useEffect(() => {
        if (isInView) {
            console.log('🎯 PlacementSuccessStory component came into view! Starting timers...')
            
            // Show the "Watch" text after 1.5 seconds when component comes into view
            const watchTimer = setTimeout(() => {
                setShowWatchText(true)
                console.log('✅ Watch text shown after 1.5 seconds')
            }, 1500)

            // Enable autoplay after 30 seconds when component comes into view
            const autoplayTimer = setTimeout(() => {
                // Manually start autoplay on the Swiper instance
                if (swiperRef.current && swiperRef.current.swiper) {
                    swiperRef.current.swiper.autoplay.start()
                    console.log('🚀 Autoplay started after 30 seconds! Slider will now move every 6 seconds')
                } else {
                    console.log('❌ Swiper ref not available for autoplay')
                }
            }, 30000)

            return () => {
                clearTimeout(watchTimer)
                clearTimeout(autoplayTimer)
            }
        }
    }, [isInView])

    // Control video playback based on visibility with delay
    useEffect(() => {
        if (videoRef.current) {
            if (isVideoInView && !isVideoPlaying) {
                // Video comes into view and is not playing - start delayed play
                console.log('🎬 Video entering view - starting delayed play...');
                
                // Clear any existing timeout
                if (playTimeoutRef.current) {
                    clearTimeout(playTimeoutRef.current);
                }
                
                // Set 1 second delay before playing
                playTimeoutRef.current = setTimeout(() => {
                    if (videoRef.current && isVideoInView) {
                        videoRef.current.play().then(() => {
                            setIsVideoPlaying(true);
                            console.log('✅ Video started playing after delay');
                        }).catch(error => {
                            console.log('❌ Video play failed:', error);
                        });
                    }
                }, 1000);
                
            } else if (!isVideoInView && isVideoPlaying) {
                // Video goes out of view and is playing - pause immediately
                console.log('⏸️ Video leaving view - pausing immediately');
                
                // Clear the play timeout if video is leaving view
                if (playTimeoutRef.current) {
                    clearTimeout(playTimeoutRef.current);
                    playTimeoutRef.current = null;
                }
                
                // Pause immediately
                videoRef.current.pause();
                setIsVideoPlaying(false);
            }
        }
    }, [isVideoInView, isVideoPlaying]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (playTimeoutRef.current) {
                clearTimeout(playTimeoutRef.current);
            }
        };
    }, []);

    // Add scroll handler for modal content
    const handleModalScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setIsScrolledDown(isAtBottom);
    };

    // Add scroll functions for arrow container
    const scrollToTop = () => {
        if (modalContainerRef.current) {
            modalContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    const scrollToBottom = () => {
        if (modalContainerRef.current) {
            modalContainerRef.current.scrollTo({
                top: modalContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    const handleArrowContainerClick = () => {
        if (isScrolledDown) {
            scrollToTop();
        } else {
            scrollToBottom();
        }
    };

    // Add mute toggle function
    const toggleMute = () => {
        setIsMuted(prev => !prev)
    }

    // Handle video play/pause state changes
    const handleVideoStateChange = (playing) => {
        setIsModalVideoPlaying(playing);
    };

    return (
        <div className='w-full'>
            <Swiper
                ref={swiperRef}
                spaceBetween={12}
                slidesPerView={1.8}
                loop={true}
                autoplay={{
                    delay: 4000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false
                }}
                speed={2000}
                navigation={false}
                modules={[Autoplay]}
                className="mySwiper"
                breakpoints={{
                    768: {
                        slidesPerView: 3.2,
                        spaceBetween: 16,
                    }
                }}
                onSwiper={(swiper) => {
                    // Initially stop autoplay
                    swiper.autoplay.stop()
                    console.log(' Swiper initialized, autoplay stopped')
                }}
            >
                {placementSuccessStories?.length > 0 && placementSuccessStories?.map((item, index) => (
                    <SwiperSlide key={index}>
                        <div className='w-full p-[2px] rounded-xl' style={{backgroundImage: 'linear-gradient(143deg, #406AFF 0% , #2BBD13 100%'}} onClick={()=> {
                            setModalEnabled(()=> ({is_show : true , achievement : item}))
                        }}>
                            <div className="rounded-xl flex relative w-full h-full flex-col items-center justify-center font-light cursor-pointer" style={{backgroundImage: 'linear-gradient(to bottom, #25387C 0% , #070A16 100%'}}>
                                <div className='relative overflow-hidden h-[220px] md:h-[200px] rounded-t-xl w-full'>
                                    {index === 0 ? (
                                        <video 
                                            ref={videoRef} // Add ref to video
                                            src={'/Shweta.mp4'} 
                                            className="w-full h-[220px] md:h-[200px] object-cover object-top" 
                                            muted 
                                            loop 
                                            playsInline
                                            // Video starts paused by default
                                        />
                                    ) : (
                                        <img src={item?.thumbNail} className="w-full h-[220px] md:h-[200px] object-cover" loading='lazy' alt='' />
                                    )}
                                    {/* <h3 className='absolute bottom-1 z-50 left-3 font-medium text-white text-[14px] md:text-[16px]'>{item?.name}</h3> */}
                                    <div className='absolute z-50 px-3 bottom-2 flex justify-evenly items-center text-white text-[12px] md:text-[14px] font-semibold items-start w-full gap-2'>
                                        <div className='flex-1 leading-4 line-clamp-2'>
                                            <p>{item.from}</p>
                                        </div>
                                        <FontAwesomeIcon icon={faArrowRight} size="lg" className='text-white' />
                                        <div className='flex-1 text-end line-clamp-2'>
                                            <p>{item.to}</p>
                                        </div>
                                    </div>
                                   {index!=0 && <img src="https://assets.learntube.ai/files/Academy%20bundle/streamline_volume-mute.svg" width={24} height={24} className='absolute top-1 right-1 '/>}
                                   {index == 0 && (
                                       <div ref={firstSlideRef}>
                                           <motion.div 
                                               className="absolute top-1 left-1 bg-black/20 backdrop-blur-[4px] text-[12px] font-medium text-white flex gap-1 justify-center items-center overflow-hidden rounded-full h-8"
                                               initial={{ width: 32 }}
                                               animate={{ 
                                                   width: showWatchText ? 80 : 32
                                               }}
                                               transition={{ 
                                                   duration: 0.6, 
                                                   ease: "easeOut"
                                               }}
                                           >
                                               <motion.div
                                                   initial={{ opacity: 1, scale: 1 }}
                                                   animate={{ opacity: 1, scale: 1 }}
                                                   transition={{ duration: 0 }}
                                               >
                                                   <FontAwesomeIcon icon={faPlay} className='text-white z-50' size="md"/>
                                               </motion.div>
                                               
                                               <AnimatePresence>
                                                   {showWatchText && (
                                                       <motion.span
                                                           initial={{ opacity: 0, x: -10 }}
                                                           animate={{ opacity: 1, x: 0 }}
                                                           exit={{ opacity: 0, x: -10 }}
                                                           transition={{ 
                                                               duration: 0.4, 
                                                               ease: "easeOut"
                                                           }}
                                                           className="whitespace-nowrap"
                                                       >
                                                           Watch
                                                       </motion.span>
                                                   )}
                                               </AnimatePresence>
                                           </motion.div>
                                       </div>
                                   )}
                                </div>
                                
                                   
                                   <div className='p-3 w-full flex justify-between items-center'>
                                    <div className='font-bold text-[14px] md:text-[16px] text-left text-white/50 line-clamp-1'>
                                        {item?.name}
                                    </div>
                                    <img src={item?.companyLogo} width={'auto'} height={24} className='h-6 md:h-8'/>

                                   </div>
                                
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            {modalEnabled?.is_show &&   <Dialog open={modalEnabled?.is_show} 
            onClose={() => null}
                as="div"
                style={{ zIndex: '9999999999' }}
                className="fixed inset-0 z-10 my-auto justify-center flex-col items-center">
                <Dialog.Overlay className="fixed inset-0 my-auto bg-black/70 backdrop-blur-[10px] z-20" />
                <Dialog.Title />
                <Dialog.Description />

                <div className='w-full h-full my-auto flex flex-col justify-center items-center'>
                    <div ref={modalContainerRef} className='relative z-50 w-[90%] max-w-lg max-h-[600px] overflow-y-scroll rounded-2xl text-white p-[1px]' 
                         style={{backgroundImage:  `linear-gradient(
    to right,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0.3) 100%
    )`}}
                         onScroll={handleModalScroll}> {/* Add this line */}
        <div className="rounded-2xl   " style={{
                            background: 'radial-gradient(53.11% 56.94% at 50% 50%, #26496A 0%, #182C3F 100%)',
                            boxShadow: '0px 0px 20px 0px #0000004D'
                        }}>
                       
                       <div className={`relative w-full ${!modalEnabled?.achievement?.videoUrl?.includes('shorts') ? 'h-[320px]' : 'h-[600px]'}`}>
    <ReactPlayer 
        url={modalEnabled?.achievement?.videoUrl} 
        width='100%'
        height='100%'
        playing={true}
        loop={true}
        controls={false}
        muted={isMuted}
        onPlay={() => handleVideoStateChange(true)}
        onPause={() => handleVideoStateChange(false)}
        config={{
            youtube: {
                playerVars: {
                    disablekb: 1,
                    fs: 0,
                    modestbranding: 1,
                    rel: 0
                }
            },
            file: {
                attributes: {
                    preload: 'metadata',
                    controlsList: 'nodownload',
                    disablePictureInPicture: true
                }
            }
        }}
    />
    <div 
        className="absolute bottom-0 right-0 w-full h-full pointer-events-none"
        style={{ zIndex: 999999 }}
    >
        <button
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Button clicked!');
                toggleMute();
            }}
            onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Touch start');
            }}
            onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Touch end - toggling mute');
                toggleMute();
            }}
            className={`absolute   bottom-4 right-4 w-14 h-14 rounded-full flex items-center justify-center border-none cursor-pointer pointer-events-auto outline-none ${
                modalEnabled?.achievement?.videoUrl?.includes('shorts')  ? ( isModalVideoPlaying  ? 'mt-3 ml-[calc(100%-35px)]' : 'mt-12 ml-[calc(100%-35px)]' ) : ('mt-3 ml-[calc(100%-35px)]')
            }`}
            style={{
                zIndex: 10000000000000000, // Higher z-index than overlay
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
                touchAction: 'manipulation',
                position: 'relative' // Ensure button is positioned correctly
            }}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
            <FontAwesomeIcon 
                icon={isMuted ? faVolumeMute : faVolumeUp} 
                size="md"
                className="text-white"
                style={{ pointerEvents: 'none' }} // Prevent icon from intercepting events
            />
        </button>
    </div>
</div>
                        
                        {/* Separate mute button overlay - positioned outside ReactPlayer container */}
                      

                        <div className='p-3'>
                            
                                <div className='w-full flex justify-center items-center gap-2'>
                                    <h3 className='font-bold text-[16px]'>{modalEnabled?.achievement?.name}</h3>
                                    <img width={'auto'} height={24} src={modalEnabled?.achievement?.companyModalLogo} alt="" className='w-auto h-[24px]'/>
                                </div>
                            
                            <div className='flex justify-evenly items-center text-[14px] text-white font-medium w-full px-2 gap-2 mt-3'>
                                <div className='leading-5 flex-1'>
                                    <p>{modalEnabled?.achievement?.from}</p>
                                </div>
                                <img src="https://assets.learntube.ai/files/Post%20Payment/Arrow.png?updatedAt=1704522242977" width={26} height={26} alt="" className='w-auto h-[26px]' />
                                <div className='flex-1 text-end leading-5'>
                                    <p>{modalEnabled?.achievement?.to}</p>
                                </div>
                            </div>
                            <div className='flex flex-col gap-2 mt-4 text-[12px] text-white/80'>
                                {modalEnabled?.achievement?.description?.split('\n')?.map((item, index) => (
                                    <p key={index} className='leading-[18px]'>{item}</p>
                                ))}
                            </div>
                        </div>

            </div>
                    </div>
                    
                    {/* Animated Three-Arrow Scroll Indicator */}
                    <motion.div 
                        className="z-50 -mt-4 flex justify-center"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div 
                            className=" bg-[#182C3F] px-3 py-0 rounded-lg flex flex-col -space-y-4 cursor-pointer hover:bg-[#1f3a4f] transition-colors duration-200"
                            onClick={handleArrowContainerClick}
                        >
                            {/* Three stacked arrows with sequential opacity wave animation */}
                            {/* Top arrow - starts wave first */}
                            <motion.div
                                animate={{ 
                                    opacity: isScrolledDown ? 
                                        [0.2, 1, 0.2] : // Upward wave (reversed)
                                        [1, 0.2, 1]      // Downward wave
                                }}
                                transition={{ 
                                    opacity: { 
                                        duration: 2, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        times: [0, 0.5, 1]
                                    }
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isScrolledDown ? faChevronUp : faChevronDown} 
                                    size="sm"
                                    className="text-[#70AEFF]"
                                />
                            </motion.div>
                            
                            {/* Middle arrow - starts wave second */}
                            <motion.div
                                animate={{ 
                                    opacity: isScrolledDown ? 
                                        [0.2, 1, 0.2] : // Upward wave (reversed)
                                        [0.2, 1, 0.2]   // Downward wave
                                }}
                                transition={{ 
                                    opacity: { 
                                        duration: 2, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        times: [0, 0.5, 1],
                                        delay: 0.7
                                    }
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isScrolledDown ? faChevronUp : faChevronDown} 
                                    size="sm"
                                    className="text-[#70AEFF]"
                                />
                            </motion.div>
                            
                            {/* Bottom arrow - starts wave last */}
                            <motion.div
                                animate={{ 
                                    opacity: isScrolledDown ? 
                                        [0.2, 1, 0.2] : // Upward wave (reversed)
                                        [0.2, 1, 0.2]   // Downward wave
                                }}
                                transition={{ 
                                    opacity: { 
                                        duration: 2, 
                                        repeat: Infinity, 
                                        ease: "easeInOut",
                                        times: [0, 0.5, 1],
                                        delay: 1.4
                                    }
                                }}
                            >
                                <FontAwesomeIcon 
                                    icon={isScrolledDown ? faChevronUp : faChevronDown} 
                                    size="sm"
                                    className="text-[#70AEFF]"
                                />
                            </motion.div>
                        </div>
                    </motion.div>
                    
                    <div className="z-50 mt-4 bg-white/20 rounded-full w-[32px] h-[32px] flex justify-center items-center cursor-pointer" onClick={() => {
                        setIsMuted(false)
                        setIsModalVideoPlaying(true)
                        setIsScrolledDown(false); setModalEnabled((prev)=> ({...prev , is_show : false})) }}>
                            <img src="https://assets.learntube.ai/files/recognised_section/X.png?updatedAt=1746165712541" className='w-[28px] h-[28px]' alt='' />
                    </div>
                </div>
            </Dialog>}
        </div>
    )
}

export default SuccessStory